import React, { useContext, useEffect, useRef, useState } from 'react';
import { Container } from '../components/DnDContainer';
import { UserContext } from '@/routes';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button } from '@/components/Elements';
import { useNavigate, useParams } from 'react-router-dom';
import { useRefValue } from '@/features/services/api/getServices';
import { DndProvider } from 'react-dnd';

import { Heading } from '@/components/Elements/Heading';
import { Pets } from '@/features/customers';
import { showNotification } from '@/utils/notificationUtils';
import { PetListDetails } from '@/features/availability/components/petListDetails';
import { Insurance } from '../components/InsuranceFieldComponent';
import appCompoLoading from '@/assets/animation/loading.json';

import { Address, FormField, FormFieldGroup, Vaccine, VaccineFormData } from '../types';
import {
  CustomerFormData,
  MyFormFieldsResponse,
  SaveFormFieldData,
  useGetMyIntakeForm,
  useSaveCustomerResponse,
  useGetCustomerSubmissionById,
} from '../api/getFields';
import { useAuth } from '@/lib/auth';

import { ReactComponent as AddIcon } from '@/assets/img/add-icon-red.svg';
import { ReactComponent as AppLogo } from '@/assets/img/p-logo.svg';
import { ReactComponent as MenuBackIcon } from '@/assets/img/menu-back-icon.svg';
import './index.scss';
import { useTranslation } from 'react-i18next';
import Utils from '@/utils/util';
import ThankYouComponent from '../components/ThankYouComponent';
import Lottie from 'lottie-react';

export const IntakeForm = () => {
  const { formId, email, phone, slug, readOnly, responseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();
  const userContext = useContext(UserContext);
  const useRefEvents = useRefValue();
  const getIntakeFormMutation = useGetMyIntakeForm();
  const getCustomerSubmissionMutation = useGetCustomerSubmissionById();
  const saveCustomerDataMutation = useSaveCustomerResponse();
  const [selectedFormFieldGroup, setSelectedFormFieldGroup] = useState<FormFieldGroup[]>([]);
  const [formHeading, setFormHeading] = useState<string>(t('Petsked Form Heading'));
  const [formDescription, setFormDescription] = useState<string>(t('Petsked form description'));
  const [additionaPetFFG, setAdditionaPetFFG] = useState<FormFieldGroup[]>([]);
  const [formFieldResponse, setFormFieldResponse] = useState<SaveFormFieldData>();
  const [selectedPetIndex, setSelectedPetIndex] = useState(-1);
  const [isReadonly] = useState(user && readOnly === 'read' ? true : false);
  const [formDetails, setFormDetails] = useState<any>();
  const [showThankyouMsg, setShowThankyouMsg] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  useEffect(() => {
    getRefValues();
  }, []);

  useEffect(() => {
    if (formDetails) {
      getIntakeForms(formDetails.intakeForm);
    }
  }, [formDetails]);

  const getRefValues = async () => {
    const res = await useRefEvents.mutateAsync({
      businessId: 1,
    });
    const breedList =
      res &&
      res.breed_list.map((item: any) => ({
        label: item,
        value: item,
        isSelected: false,
      }));
    const colorList =
      res &&
      res.pet_color_list.map((item: any) => ({
        label: item,
        value: item,
        isSelected: false,
      }));
    userContext?.setReferenceValues({
      breeds: breedList,
      colors: colorList,
    });
    if (!slug) {
      getCustomerReponse();
    } else {
      getIntakeForms();
    }
  };
  const getCustomerReponse = async () => {
    const customerFormResponse = await getCustomerSubmissionMutation.mutateAsync({
      responseId: responseId,
    });
    setFormDetails({
      owner: customerFormResponse?.response?.owner,
      pets: customerFormResponse?.response?.pets,
      intakeForm: customerFormResponse.intakeForm,
    });
  };
  const getIntakeForms = async (data?: any) => {
    let intakeFormRes: SaveFormFieldData | undefined = undefined;
    if (slug) {
      const res = (await getIntakeFormMutation.mutateAsync({
        slug: slug,
      })) as MyFormFieldsResponse;
      intakeFormRes = res.form;
      if (userContext) {
        userContext.setBussiness(res.business);
      }
    } else if (data) {
      intakeFormRes = data as SaveFormFieldData;
    }
    setFormFieldResponse(JSON.parse(JSON.stringify(intakeFormRes)));
    const formFieldGroups: FormFieldGroup[] = [];
    const ownerFields = getFormFieldsSortedByIndex('owner', intakeFormRes?.fields as FormField[]);
    intakeFormRes && setFormHeading(intakeFormRes?.formTitle);
    intakeFormRes && setFormDescription(intakeFormRes?.formDescription);

    if (isReadonly) {
      userContext &&
        userContext.setConfig({
          intakeViewMode: true,
        });
      populateOwnerFormFields(ownerFields, formDetails?.owner);
    }
    ownerFields.length &&
      formFieldGroups.push({
        formFields: ownerFields,
        key: 'owner',
        name: t('Owner Details'),
      });

    const petFields = getFormFieldsSortedByIndex('pet', intakeFormRes?.fields as FormField[]);
    populatePetBasicFields(petFields);
    if (isReadonly && formDetails?.pets.length > 0) {
      populatePetFormFields(petFields, formDetails?.pets[0]);
      if (formDetails?.pets.length > 0) {
        getAdditonalPetDetails(petFields, formDetails?.pets);
      }
    }
    const ffg: FormFieldGroup = {
      formFields: [...petFields],
      key: 'pet',
      name: t('Pet Details'),
    };
    petFields.length && formFieldGroups.push(ffg);
    setSelectedFormFieldGroup(formFieldGroups);
    setIsDataLoading(false);
  };

  const populateOwnerFormFields = (formFields: FormField[], data: any) => {
    formFields.forEach(
      (ff) => (ff.fieldValue = data[ff.fieldName] ? data[ff.fieldName] : undefined)
    );
  };

  const populatePetBasicFields = (formFields: FormField[]) => {
    formFields.forEach((ff) => {
      if (ff.fieldType === 'petbasic') {
        const defaultPetType = Utils.getPetTypes(
          user && user.offerings
            ? user.offerings?.petTypes
            : userContext?.business?.offerings?.petTypes
        );
        ff.fieldValue = {
          name: '',
          breed: Utils.BREED_UNSPECIFIED,
          petType: defaultPetType ? defaultPetType : '',
          petSize: Utils.DEFAULT_PET_SIZE,
          color: '',
        };
      }
    });
  };
  const populatePetFormFields = (formFields: FormField[], petData: any) => {
    formFields.forEach((ff) => {
      if (ff.fieldType === 'petvaccine') {
        const vaccineFormData: VaccineFormData = {
          vaccineList: petData[ff.fieldName] ? petData[ff.fieldName] : [],
        };
        ff.fieldValue = vaccineFormData;
      } else {
        ff.fieldValue = petData[ff.fieldName] ? petData[ff.fieldName] : undefined;
      }
    });
  };

  const getAdditonalPetDetails = (formFields: FormField[], pets: any[]) => {
    const additionalPets: FormFieldGroup[] = [];
    pets.forEach((pet) => {
      const clonedFormFields = JSON.parse(JSON.stringify(formFields));
      clonedFormFields.forEach((ff: FormField) => {
        if (ff.fieldType === 'petvaccine') {
          const vaccineFormData: VaccineFormData = {
            vaccineList: pet[ff.fieldName] ? pet[ff.fieldName] : [],
          };
          ff.fieldValue = vaccineFormData;
        } else {
          ff.fieldValue = pet[ff.fieldName] ? pet[ff.fieldName] : undefined;
        }
      });
      const ffg: FormFieldGroup = {
        formFields: [...clonedFormFields],
        key: 'pet',
        name: t('Pet Details'),
      };
      additionalPets.push(ffg);
    });
    setSelectedPetIndex(0);
    setAdditionaPetFFG(additionalPets);
  };

  const getFormFieldsSortedByIndex = (type: 'owner' | 'pet', formFields: FormField[]) => {
    const filteredFormFields = formFields.filter((ff) => ff.category === type);
    filteredFormFields.sort((a, b) => {
      return a.orderIndex - b.orderIndex;
    });
    return filteredFormFields;
  };

  const onFormFieldsChange = (sffg: FormFieldGroup, formFields: FormField[]) => {
    const filteredFieldGroupIndex = selectedFormFieldGroup.findIndex((ffg) => ffg.key === sffg.key);
    if (formFields.length > 0) {
      sffg.formFields = [...formFields];
      selectedFormFieldGroup.splice(filteredFieldGroupIndex, 1, { ...sffg });
    } else {
      sffg.formFields = [];
      selectedFormFieldGroup.splice(filteredFieldGroupIndex, 1);
    }
    setSelectedFormFieldGroup(selectedFormFieldGroup.length > 0 ? [...selectedFormFieldGroup] : []);
  };

  const onFormFieldValueChange = (formField: FormField) => {
    const filteredFieldGroup = selectedFormFieldGroup.find((sfg) => sfg.key === formField.category);
    if (filteredFieldGroup) {
      const formFields = filteredFieldGroup.formFields;
      if (formFields && formFields.length) {
        const filteredIndex = formFields.findIndex((ff) => ff.fieldName === formField.fieldName);
        if (filteredIndex >= 0) {
          formFields.splice(filteredIndex, 1, formField);
        }
      }
    }
    setSelectedFormFieldGroup([...selectedFormFieldGroup]);
  };
  const saveEndUserData = async () => {
    try {
      const pets = [];
      if (additionaPetFFG.length) {
        additionaPetFFG.forEach((ffg) => {
          const formData: CustomerFormData = {};
          const formFields = ffg.formFields;
          formFields.forEach((ff) => {
            if (ff.fieldType === 'petvaccine') {
              const vaccineFormData = ff.fieldValue as VaccineFormData;
              let vaccineList: Vaccine[] = [];
              if (vaccineFormData) {
                if (vaccineFormData.vaccineList && vaccineFormData.vaccineList.length) {
                  vaccineList = vaccineFormData.vaccineList;
                }
                if (
                  vaccineFormData.vaccine &&
                  vaccineFormData.vaccine.takenDate &&
                  vaccineFormData.vaccine.expiryDate
                ) {
                  vaccineList.push(vaccineFormData.vaccine);
                }
              }
              formData[ff.fieldName] = vaccineList as any;
            } else {
              formData[ff.fieldName] = ff.fieldValue;
            }
          });

          pets.push(formData);
        });
      }
      const ownerData = getFormData('owner');
      if (!ownerData) {
        return;
      }
      if (selectedPetIndex === -1 && pets.length === 0) {
        const petData = getFormData('pet');
        if (petData) {
          pets.push(petData);
        } else {
          return;
        }
      }
      const errorMessage = validateTermsAndCondition();
      if (errorMessage) {
        return;
      }
      const formData = {
        request: {
          owner: ownerData,
          pets: pets,
        },
        slug: slug ? slug : '',
      };
      //return;
      await saveCustomerDataMutation.mutateAsync(formData);
      setShowThankyouMsg(true);
      clearFormData();
    } catch (e) {
      console.log('Error Catched', e);
    }
  };
  const clearFormData = () => {
    setAdditionaPetFFG([]);
    setSelectedPetIndex(-1);
    getIntakeForms();
    scrollToTheErrorField(undefined, true);
  };
  const getFormData = (formDataType: 'owner' | 'pet') => {
    const formData: CustomerFormData = {};
    const filteredFieldGroup = selectedFormFieldGroup.find((sfg) => sfg.key === formDataType);
    if (formDataType === 'pet') {
      const errorMessage = filteredFieldGroup && validatePetDetails(filteredFieldGroup);
      if (errorMessage) {
        showNotification({
          title: errorMessage,
          type: 'error',
        });
        return;
      }
    } else {
      const errorMessage = filteredFieldGroup && validateOwnerDetails(filteredFieldGroup);
      if (errorMessage) {
        showNotification({
          title: errorMessage,
          type: 'error',
        });
        return;
      }
    }
    if (filteredFieldGroup) {
      const formFields = filteredFieldGroup.formFields;
      formFields.forEach((ff) => {
        if (ff.fieldType === 'petvaccine') {
          const vaccineFormData = ff.fieldValue as VaccineFormData;
          let vaccineList: Vaccine[] = [];
          if (vaccineFormData) {
            if (vaccineFormData.vaccineList && vaccineFormData.vaccineList.length) {
              vaccineList = vaccineFormData.vaccineList;
            }
            if (
              vaccineFormData.vaccine &&
              vaccineFormData.vaccine.takenDate &&
              vaccineFormData.vaccine.expiryDate
            ) {
              vaccineList.push(vaccineFormData.vaccine);
            }
          }
          formData[ff.fieldName] = vaccineList as any;
        } else {
          formData[ff.fieldName] = ff.fieldValue;
        }
      });
    }
    return formData;
  };

  const addAnotherPet = () => {
    const filteredFieldGroup = selectedFormFieldGroup.find((sfg) => sfg.key === 'pet');
    const addFilteredFieldGroup = JSON.parse(JSON.stringify(filteredFieldGroup));
    if (addFilteredFieldGroup) {
      const errorMessage = validatePetDetails(addFilteredFieldGroup);
      if (errorMessage) {
        showNotification({
          title: errorMessage,
          type: 'error',
        });
        return;
      }
      if (addFilteredFieldGroup) {
        selectedPetIndex >= 0
          ? additionaPetFFG.splice(selectedPetIndex, 1, { ...addFilteredFieldGroup })
          : additionaPetFFG.push({ ...addFilteredFieldGroup });
        setAdditionaPetFFG([...additionaPetFFG]);
      } else {
        setAdditionaPetFFG([{ ...addFilteredFieldGroup }]);
      }
      filteredFieldGroup && resetFormFieldGroup(filteredFieldGroup);
      setSelectedPetIndex(-1);
    }
  };

  const resetFormFieldGroup = (fieldGroup: FormFieldGroup) => {
    if (formFieldResponse) {
      const petFields = getFormFieldsSortedByIndex('pet', formFieldResponse.fields as FormField[]);

      fieldGroup.formFields = JSON.parse(JSON.stringify(petFields));
      //fieldGroup.formFields.forEach((ff) => (ff.fieldValue = undefined));
      const filteredFieldGroupIndex = selectedFormFieldGroup.findIndex((sfg) => sfg.key === 'pet');
      selectedFormFieldGroup.splice(filteredFieldGroupIndex, 1, { ...fieldGroup });
      setSelectedFormFieldGroup([...selectedFormFieldGroup]);
    }
  };

  const validatePetDetails = (filteredFieldGroup: FormFieldGroup) => {
    let errorMessage = '';
    filteredFieldGroup?.formFields.every((ff) => {
      if (ff.fieldType === 'petbasic') {
        const { name, breed, petType } = ff.fieldValue
          ? (ff.fieldValue as Pets)
          : { name: '', breed: '', petType: '' };
        if (!petType || petType.trim().length < 1) {
          errorMessage = t('Please select pet type');
          scrollToTheErrorField(ff);
        } else if (!name || name.trim().length < 1) {
          errorMessage = t('Please enter pet name');
          scrollToTheErrorField(ff);
        } else if (!breed || breed.trim().length < 1) {
          errorMessage = t('Please select the pet breed');
          scrollToTheErrorField(ff);
        }
      } else if (ff.fieldType === 'petvaccine') {
        const vaccineData = ff.fieldValue && (ff.fieldValue as VaccineFormData).vaccine;
        if (vaccineData) {
          const { takenDate, expiryDate, type, documentLink } = vaccineData;
          if (!expiryDate && !takenDate && !type && !documentLink) {
            errorMessage = '';
          } else {
            if (!type || type.trim().length < 1) {
              errorMessage = t('Please select vaccination type');
              scrollToTheErrorField(ff);
            } else if (!takenDate || takenDate.trim().length < 1) {
              errorMessage = t('Please select vaccination taken date');
              scrollToTheErrorField(ff);
            } else if (!expiryDate || expiryDate.trim().length < 1) {
              errorMessage = t('Please select vaccination expiry date');
              scrollToTheErrorField(ff);
            } else if (!documentLink || documentLink.trim().length < 1) {
              errorMessage = t('Please upload vaccination document');
              scrollToTheErrorField(ff);
            }
          }
        }
      } else if (ff.fieldType === 'petinsurance') {
        const { insuranceCopy, insuranceExpiry, insuranceInformation, insuranceName } =
          ff.fieldValue
            ? (ff.fieldValue as Insurance)
            : {
                insuranceCopy: '',
                insuranceExpiry: '',
                insuranceInformation: '',
                insuranceName: '',
              };
        if (!insuranceCopy || insuranceCopy.trim().length < 1) {
          errorMessage = t('Please upload insurance document');
          scrollToTheErrorField(ff);
        } else if (!insuranceName || insuranceName.trim().length < 1) {
          errorMessage = t('Please enter insurance details');
          scrollToTheErrorField(ff);
        } else if (!insuranceExpiry || insuranceExpiry.trim().length < 1) {
          errorMessage = t('Please enter insurance expiry date');
          scrollToTheErrorField(ff);
        }
      } else {
        if (
          ff.fieldType === 'email' ||
          ff.fieldType === 'textinput' ||
          ff.fieldType === 'textarea' ||
          ff.fieldType === 'phone'
        ) {
          if (!ff.fieldValue || ff.fieldValue.trim().length < 1) {
            errorMessage = 'Please enter ' + ff.fieldLabel;
            scrollToTheErrorField(ff);
          }
        } else {
          if (
            ff.fieldType !== 'checkbox' &&
            (!ff.fieldValue ||
              (typeof ff.fieldValue === 'string' && ff.fieldValue.trim().length < 1))
          ) {
            errorMessage = 'Please select ' + ff.fieldLabel;
            scrollToTheErrorField(ff);
          }
        }
      }
      if (errorMessage) {
        return false;
      }
      return true;
    });
    return errorMessage;
  };
  const validateOwnerDetails = (filteredFieldGroup: FormFieldGroup) => {
    let errorMessage = '';
    filteredFieldGroup?.formFields.every((ff) => {
      if (ff.fieldType !== 'terms_checkbox') {
        if (ff.fieldType === 'address') {
          const { address, country, zipCode } = ff.fieldValue
            ? (ff.fieldValue as Address)
            : { address: '', country: '', zipCode: '' };
          if (!address || address.trim().length < 1) {
            errorMessage = t('Please enter address');
            scrollToTheErrorField(ff);
          } else if (!country || country.trim().length < 1) {
            errorMessage = t('Please select country');
            scrollToTheErrorField(ff);
          } else if (!zipCode || zipCode.trim().length < 1) {
            errorMessage = t('Please enter zip code');
            scrollToTheErrorField(ff);
          }
        } else {
          if (
            ff.fieldType === 'email' ||
            ff.fieldType === 'textinput' ||
            ff.fieldType === 'textarea' ||
            ff.fieldType === 'phone'
          ) {
            if (!ff.fieldValue || ff.fieldValue.trim().length < 1) {
              errorMessage = 'Please enter ' + ff.fieldLabel;
              scrollToTheErrorField(ff);
            } else if (ff.fieldType === 'email' && !Utils.isValidEmail(ff.fieldValue)) {
              errorMessage = 'Please enter valid email address';
              scrollToTheErrorField(ff);
            }
          } else {
            if (
              ff.fieldType !== 'checkbox' &&
              (!ff.fieldValue ||
                (typeof ff.fieldValue === 'string' && ff.fieldValue.trim().length < 1))
            ) {
              errorMessage = 'Please select ' + ff.fieldLabel;
              scrollToTheErrorField(ff);
            }
          }
        }
        if (errorMessage) {
          return false;
        }
      }
      return true;
    });
    return errorMessage;
  };

  const validateTermsAndCondition = () => {
    const filteredFieldGroup = selectedFormFieldGroup.find((sfg) => sfg.key === 'owner');
    let errorMessage = '';
    const tcf = filteredFieldGroup?.formFields.find((ff) => ff.fieldType === 'terms_checkbox');
    if (tcf) {
      if (tcf.fieldType === 'terms_checkbox') {
        if (!tcf.fieldValue || tcf.fieldValue.trim().length < 1) {
          errorMessage = 'Please accept ' + tcf.fieldLabel;
          scrollToTheErrorField(tcf);
        }
      }
    }
    if (errorMessage) {
      showNotification({
        title: errorMessage,
        type: 'error',
      });
      return true;
    }
    return false;
  };
  const scrollToTheErrorField = (formField?: FormField, remove = false) => {
    const errorField = document.getElementById(
      `intake-field-${formField ? formField.id : 1}`
    ) as HTMLElement;
    if (errorField) {
      errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const petskedForm = document.getElementById('petsked-form-section') as HTMLElement;
    if (remove) {
      const scrollElement: any = document.getElementById('form-group-heading-0');
      scrollElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      petskedForm?.classList?.remove('petsked-error'); // Add class if input is empty
    } else {
      petskedForm?.classList?.add('petsked-error'); // Add class if input is empty
    }
  };
  const getPetBasicData = (ffg: FormFieldGroup) => {
    const petbasic = ffg.formFields.find((ff) => ff.fieldType === 'petbasic');
    if (petbasic) {
      return petbasic?.fieldValue;
    } else {
      return null;
    }
  };

  const onSelectEditPet = (ffg: FormFieldGroup, selectedPetIndex: number) => {
    setSelectedPetIndex(selectedPetIndex);
    const filteredFieldGroupIndex = selectedFormFieldGroup.findIndex((sfg) => sfg.key === 'pet');
    if (filteredFieldGroupIndex >= 0) {
      const selectedFfg = selectedFormFieldGroup[filteredFieldGroupIndex];
      selectedFfg.formFields = JSON.parse(JSON.stringify(ffg.formFields));
      selectedFormFieldGroup.splice(filteredFieldGroupIndex, 1, { ...selectedFfg });
      setSelectedFormFieldGroup([...selectedFormFieldGroup]);
    }
  };

  const deletePet = (ffg: FormFieldGroup, selectedPetIndex: number) => {
    additionaPetFFG.splice(selectedPetIndex, 1);
    setAdditionaPetFFG([...additionaPetFFG]);
    if (selectedPetIndex >= 0) {
      const filteredFieldGroup = selectedFormFieldGroup.find((sfg) => sfg.key === 'pet');
      setSelectedPetIndex(-1);
      filteredFieldGroup && resetFormFieldGroup(filteredFieldGroup);
    }
  };
  if (isDataLoading) {
    return (
      <div className="loader-intake h-full w-full flex items-center justify-center">
        <Lottie className="hw-60" animationData={appCompoLoading} />
      </div>
    );
  }
  return (
    <>
      {selectedFormFieldGroup.length > 0 && (
        <DndProvider backend={HTML5Backend}>
          <section className={`intake-form-section live-form`}>
            <div className={`intake-form-creation live ${isReadonly ? 'readonly' : ''}`}>
              <div id="petsked-form-section" className="bottom-form-section">
                <>
                  {showThankyouMsg ? (
                    <ThankYouComponent onBackClicked={() => setShowThankyouMsg(false)} />
                  ) : (
                    <>
                      <div className="form-group">
                        {isReadonly && (
                          <div
                            className="menu-back-nav"
                            onClick={() =>
                              navigate(`/customer-details/${phone}/?selected-tab=intakeform`)
                            }
                          >
                            <MenuBackIcon /> <span>{t('Back')}</span>
                          </div>
                        )}
                        <div className="form-title">
                          <Heading title={formHeading} description={formDescription} />
                        </div>
                      </div>
                      {selectedFormFieldGroup.map((sffg, index) =>
                        sffg.formFields.length > 0 ? (
                          <div className="form-group">
                            <div id={`form-group-heading-${index}`} className="form-group-heading">
                              <h6>{sffg.name}</h6>
                            </div>
                            {sffg.key === 'pet' && additionaPetFFG && additionaPetFFG.length > 0 && (
                              <div className="wrapper no-border-bottom">
                                <div className="new-pet-list">
                                  {additionaPetFFG.map((ffg: FormFieldGroup, index: number) => {
                                    const pet: Pets = getPetBasicData(ffg);
                                    return pet ? (
                                      <PetListDetails
                                        key={`new-pet-${index}`}
                                        data={pet}
                                        showDeleteOption={isReadonly ? false : true}
                                        isSelected={selectedPetIndex === index}
                                        onChange={() => onSelectEditPet(ffg, index)}
                                        onDelete={() => deletePet(ffg, index)}
                                      />
                                    ) : (
                                      <></>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            <Container
                              disabled={isReadonly ? true : false}
                              formFields={sffg.formFields}
                              onChange={(fFields) => onFormFieldsChange(sffg, fFields)}
                              onFieldValueChange={isReadonly ? () => {} : onFormFieldValueChange}
                              disableDrag={true}
                              preview={true}
                              key={`sffg.key-${index}`}
                              className={sffg.key === 'pet' ? 'no-border-bottom' : ''}
                            />
                          </div>
                        ) : (
                          <></>
                        )
                      )}
                      {!isReadonly && (
                        <>
                          <div className="form-group">
                            <div className="wrapper no-border-top p-0">
                              <div className="action-pet">
                                <Button
                                  variant="inverse"
                                  size="lg"
                                  onClick={addAnotherPet}
                                  startIcon={selectedPetIndex == -1 ? <AddIcon /> : undefined}
                                >
                                  {selectedPetIndex == -1
                                    ? t('Add another pet')
                                    : t('Update selected pet')}
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div className="form-group">
                            <div className="form-action-submit">
                              <div className="left-section"></div>
                              <div className="right-section">
                                <Button variant="nobg" size="lg" onClick={clearFormData}>
                                  {t('Clear Form')}
                                </Button>
                                <Button
                                  variant="primary"
                                  size="lg"
                                  disabled={saveCustomerDataMutation.isLoading}
                                  onClick={saveEndUserData}
                                >
                                  {saveCustomerDataMutation.isLoading
                                    ? t('Submitting...')
                                    : t('Submit Form')}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              </div>
            </div>
            {!isReadonly && (
              <footer>
                <div className="left-section">
                  <span>© 2024 {t('Petsked all rights reserved')}</span>
                </div>
                <div className="right-section">
                  <span>{t('This form is created using')}</span>{' '}
                  <span>
                    <AppLogo />
                  </span>
                </div>
              </footer>
            )}
          </section>
        </DndProvider>
      )}
    </>
  );
};
